import { HvacArea, NativeError, RuntimeError } from '@belimo-retrofit-portal/logic';
import { SagaIterator } from 'redux-saga';
import { BASEDATA_CHANGE } from 'src/modules/basedata/actions/BasedataActions';
import { JSON_VALUE } from 'src/modules/common/codecs/JsonValue';
import { resolveInitialProjectData } from 'src/modules/common/sagas/utils/resolveProjectData';
import { Project } from 'src/modules/common/types/Project';
import { ProjectSettingsKey } from 'src/modules/common/types/ProjectSettingsKey';
import { sentryCatch } from 'src/modules/config/utils/sentryCatch';
import { showNotification } from 'src/modules/notifications/sagas/showNotification';
import { syncProjectBackground } from 'src/modules/project-sync/sagas/utils/syncProjectBackground';
import { PROJECT_VIEW_SETTINGS } from 'src/modules/project-view/actions/ProjectViewActions';
import { getProjectDetails } from 'src/modules/project-view/selectors/getProjectDetails';
import { getProjectSettings } from 'src/modules/project-view/selectors/getProjectSettings';
import { logDebug, logError } from 'src/sagas/utils/logging';
import { GetTriggerActionType } from 'src/utils/createTrigger';
import { call, put, select } from 'typed-redux-saga';

export function* basedataChangeSaga(
  action: GetTriggerActionType<typeof BASEDATA_CHANGE>,
): SagaIterator<void> {
  const formData = action.data;

  try {
    yield* call(logDebug, 'Saving project basedata...', formData);

    const previous = yield* select(getProjectDetails);
    const data = yield* call(resolveInitialProjectData, {
      ...previous.data,
      cover: formData.cover,
      title: formData.title,
      goals: formData.projectGoals,
      building: formData.building,
      participants: formData.participants,
      status: formData.status,
      statusComment: formData.statusComment,
    });
    const modified: Project = {
      ...previous,
      data: {
        ...data,
        hvac: {
          ...data.hvac,
          areas: {
            ...data.hvac.areas,
            [HvacArea.COOLING]: {
              ...data.hvac.areas[HvacArea.COOLING],
              enabled: true,
            },
            [HvacArea.HEATING]: {
              ...data.hvac.areas[HvacArea.HEATING],
              enabled: true,
            },
            [HvacArea.VENTILATION]: {
              ...data.hvac.areas[HvacArea.VENTILATION],
              enabled: true,
            },
          },
        },
      },
    };
    const previousSettings = yield* select(getProjectSettings);
    yield* put(PROJECT_VIEW_SETTINGS.trigger({
      ...previousSettings,
      [ProjectSettingsKey.ANNUAL_SAVING]: JSON_VALUE.encode(
        formData.annualSaving ? formData.annualSaving.toNumber() : null,
      ),
    }, previous.id));

    const reloaded = yield* call(syncProjectBackground, modified);
    yield* call(logDebug, 'Saving project basedata... done', reloaded);
  } catch (error) {
    const wrapped = new RuntimeError('Could not save project basedata', { formData }, NativeError.wrap(error));
    yield* call(logError, 'Saving project basedata... error', error);

    yield* call(sentryCatch, wrapped);
    yield* call(showNotification, { variant: 'error', type: 'error' });
  }
}
